import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import FieldMappingEditor from './FieldMappingEditor';
import ImportPreviewTable from './ImportPreviewTable';

const STEPS = ['upload', 'mapping', 'preview', 'result'];

const CSVImportDialog = ({ open, onClose, onImportComplete }) => {
  const { t } = useTranslation();

  // Tab state (0 = customers, 1 = parts, 2 = jobs)
  const [activeTab, setActiveTab] = useState(0);
  const entityType = activeTab === 0 ? 'customer' : activeTab === 1 ? 'part' : 'job';

  // Step state
  const [currentStep, setCurrentStep] = useState(0);

  // File and parsing state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mapping state
  const [fieldMapping, setFieldMapping] = useState({});
  const [fixedValues, setFixedValues] = useState([]); // [{targetField, value}]
  const [targetFields, setTargetFields] = useState([]);

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewSummary, setPreviewSummary] = useState(null);

  // Import result state
  const [importResult, setImportResult] = useState(null);

  // Async import progress state
  const [importProgress, setImportProgress] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef(null);

  // Reset state when dialog opens/closes or tab changes
  useEffect(() => {
    if (open) {
      resetState();
    }
    return () => {
      // Clean up polling on unmount
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [open]);

  const resetState = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setFileContent(null);
    setParseResult(null);
    setFieldMapping({});
    setFixedValues([]);
    setPreviewData(null);
    setPreviewSummary(null);
    setImportResult(null);
    setImportProgress(null);
    setIsPolling(false);
    setError(null);
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    resetState();
  };

  // Fetch target fields for the entity type
  useEffect(() => {
    const fetchTargetFields = async () => {
      try {
        const orgId = localStorage.getItem('currentOrganizationId');
        const response = await axios.get(`/api/csv-import/target-fields/?entity_type=${entityType}&organization=${orgId}`);
        setTargetFields(response.data.fields || []);
      } catch (err) {
        console.error('Failed to fetch target fields:', err);
      }
    };
    if (open) {
      fetchTargetFields();
    }
  }, [entityType, open]);

  // File drop handler
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadedFile(file);
    setError(null);
    setLoading(true);

    // Read file content for later use
    const reader = new FileReader();
    reader.onload = (e) => {
      setFileContent(e.target.result);
    };
    reader.readAsArrayBuffer(file);

    // Upload to server for parsing
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('organization', localStorage.getItem('currentOrganizationId'));

    try {
      const response = await axios.post('/api/csv-import/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setParseResult(response.data);
      setFieldMapping(response.data.suggested_mapping || {});
      // Restore saved fixed values from known format
      const savedFv = response.data.suggested_fixed_values || {};
      setFixedValues(
        Object.entries(savedFv).map(([targetField, value]) => ({ targetField, value }))
      );
      setCurrentStep(1); // Move to mapping step
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to parse CSV file'));
      setUploadedFile(null);
      setFileContent(null);
    } finally {
      setLoading(false);
    }
  }, [entityType, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    maxFiles: 1,
    disabled: loading,
  });

  // Handle mapping change
  const handleMappingChange = (newMapping) => {
    setFieldMapping(newMapping);
  };

  // Convert fixedValues array to dict for API
  const getFixedValuesDict = () => {
    const dict = {};
    for (const { targetField, value } of fixedValues) {
      if (targetField && value) {
        dict[targetField] = value;
      }
    }
    return dict;
  };

  // Proceed to preview
  const handlePreview = async () => {
    if (!fileContent || !fieldMapping) return;

    setLoading(true);
    setError(null);

    try {
      // Convert ArrayBuffer to base64
      const base64Content = btoa(
        new Uint8Array(fileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const fvDict = getFixedValuesDict();
      const response = await axios.post('/api/csv-import/preview/', {
        file_content_base64: base64Content,
        filename: uploadedFile.name,
        mapping: fieldMapping,
        entity_type: entityType,
        organization: localStorage.getItem('currentOrganizationId'),
        ...(Object.keys(fvDict).length > 0 && { fixed_values: fvDict }),
      });

      setPreviewData(response.data.preview || []);
      setPreviewSummary(response.data.summary || {});
      setCurrentStep(2); // Move to preview step
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to generate preview'));
    } finally {
      setLoading(false);
    }
  };

  // Poll for import progress
  const pollForProgress = useCallback((importEventId) => {
    let attempts = 0;
    const maxAttempts = 600; // 30 min at 3s intervals

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setIsPolling(false);
        setError(t('import_failed'));
        return;
      }
      attempts++;

      try {
        const orgId = localStorage.getItem('currentOrganizationId');
        const response = await axios.get(`/api/csv-import/${importEventId}/progress/?organization=${orgId}`);
        const data = response.data;

        setImportProgress(data);

        if (data.status === 'completed') {
          setIsPolling(false);
          setImportResult({
            import_event_id: importEventId,
            status: data.status,
            imported_count: data.imported_count,
            updated_count: data.updated_count,
            skipped_count: data.skipped_count,
            error_count: data.error_count,
            details: data.error_details || [],
          });
          if (onImportComplete) {
            onImportComplete(data);
          }
          return;
        }

        if (data.status === 'failed') {
          setIsPolling(false);
          setError(data.error_message || t('import_failed'));
          setImportResult({
            import_event_id: importEventId,
            status: data.status,
            imported_count: data.imported_count,
            updated_count: data.updated_count,
            skipped_count: data.skipped_count,
            error_count: data.error_count,
            details: data.error_details || [],
          });
          return;
        }

        // Still processing — poll again
        pollingRef.current = setTimeout(poll, 3000);
      } catch (err) {
        // Transient error — keep polling
        pollingRef.current = setTimeout(poll, 3000);
      }
    };

    setIsPolling(true);
    poll();
  }, [onImportComplete, t]);

  // Execute import
  const handleImport = async () => {
    if (!fileContent || !fieldMapping) return;

    setLoading(true);
    setError(null);

    try {
      // Convert ArrayBuffer to base64
      const base64Content = btoa(
        new Uint8Array(fileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      const fvDict = getFixedValuesDict();
      const response = await axios.post('/api/csv-import/commit/', {
        file_content_base64: base64Content,
        filename: uploadedFile.name,
        mapping: fieldMapping,
        entity_type: entityType,
        organization: localStorage.getItem('currentOrganizationId'),
        ...(Object.keys(fvDict).length > 0 && { fixed_values: fvDict }),
        save_mapping: true,
      });

      const data = response.data;

      if (data.status === 'pending' || data.status === 'processing') {
        // Async import — move to result step and start polling
        setImportProgress({
          status: data.status,
          total_rows: data.total_rows,
          processed_rows: 0,
          progress_percent: 0,
          imported_count: 0,
          updated_count: 0,
          error_count: 0,
        });
        setCurrentStep(3);
        setLoading(false);
        pollForProgress(data.import_event_id);
      } else {
        // Sync import (customers) — result is immediately available
        setImportResult(data);
        setCurrentStep(3);
        setLoading(false);
        if (onImportComplete) {
          onImportComplete(data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to execute import'));
      setLoading(false);
    }
  };

  // Go back to previous step
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  // Get step label
  const getStepLabel = (step) => {
    switch (step) {
      case 0: return t('Upload');
      case 1: return t('Field Mapping');
      case 2: return t('Preview');
      case 3: return t('Result');
      default: return '';
    }
  };

  // Check if can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!parseResult;
      case 1: return Object.keys(fieldMapping).length > 0;
      case 2: return previewSummary && (
        previewSummary.will_import > 0 || previewSummary.will_update > 0 ||
        // For large files, will_import/will_update may not be in summary
        (previewSummary.total > 0 && previewSummary.will_import === undefined)
      );
      default: return false;
    }
  };

  // Whether import is currently in progress (async)
  const isImportInProgress = isPolling || (importProgress && !importResult && importProgress.status !== 'failed');

  // Render upload step
  const renderUploadStep = () => (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        cursor: 'pointer',
        bgcolor: isDragActive ? 'action.hover' : 'background.default',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {isDragActive ? t('Drop the file here') : t('Drag and drop a CSV file here')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('or click to select a file')}
      </Typography>
    </Box>
  );

  // Render mapping step
  const renderMappingStep = () => (
    <Box>
      {parseResult && (
        <>
          {/* Format detection info */}
          <Box sx={{ mb: 3 }}>
            {parseResult.is_known_format ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                {t('Detected format')}: <strong>{parseResult.format_name}</strong>
              </Alert>
            ) : (
              <Alert severity="info">
                {parseResult.format_name
                  ? `${t('Detected format')}: ${parseResult.format_name}`
                  : t('Unknown format - mapping suggested by AI')
                }
              </Alert>
            )}
          </Box>

          {/* File info */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`${parseResult.row_count} ${t('rows')}`} variant="outlined" />
            <Chip label={`${parseResult.headers.length} ${t('columns')}`} variant="outlined" />
            {uploadedFile && <Chip label={uploadedFile.name} variant="outlined" />}
          </Box>

          {/* Mapping editor */}
          <FieldMappingEditor
            headers={parseResult.headers}
            sampleRows={parseResult.sample_rows}
            mapping={fieldMapping}
            targetFields={targetFields}
            entityType={entityType}
            onChange={handleMappingChange}
            fixedValues={fixedValues}
            onFixedValuesChange={setFixedValues}
          />
        </>
      )}
    </Box>
  );

  // Render preview step
  const renderPreviewStep = () => (
    <Box>
      {previewSummary && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {previewSummary.will_import !== undefined && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${previewSummary.will_import} ${t('will be imported')}`}
              color="success"
              variant="outlined"
            />
          )}
          {previewSummary.will_update > 0 && (
            <Chip
              icon={<EditIcon />}
              label={`${previewSummary.will_update} ${t('will be updated')}`}
              color="info"
              variant="outlined"
            />
          )}
          {previewSummary.will_skip > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${previewSummary.will_skip} ${t('will be skipped')}`}
              color="warning"
              variant="outlined"
            />
          )}
          <Chip label={`${previewSummary.total} ${t('total rows')}`} variant="outlined" />
        </Box>
      )}

      <ImportPreviewTable
        previewData={previewData}
        entityType={entityType}
      />
    </Box>
  );

  // Render result step
  const renderResultStep = () => {
    // Show progress bar when import is in progress
    if (isImportInProgress && importProgress) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            {importProgress.status === 'pending'
              ? t('import_queued')
              : t('import_in_progress')
            }
          </Typography>

          <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto', mt: 3 }}>
            <LinearProgress
              variant="determinate"
              value={importProgress.progress_percent || 0}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {importProgress.processed_rows || 0} / {importProgress.total_rows || 0} {t('rows_processed')}
              {importProgress.progress_percent > 0 && ` (${importProgress.progress_percent}%)`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            {importProgress.imported_count > 0 && (
              <Chip
                icon={<CheckCircleIcon />}
                label={`${importProgress.imported_count - (importProgress.updated_count || 0)} ${t('created')}`}
                color="success"
                size="small"
              />
            )}
            {importProgress.updated_count > 0 && (
              <Chip
                icon={<EditIcon />}
                label={`${importProgress.updated_count} ${t('updated')}`}
                color="info"
                size="small"
              />
            )}
            {importProgress.error_count > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${importProgress.error_count} ${t('errors')}`}
                color="error"
                size="small"
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {t('do_not_close_dialog')}
          </Typography>
        </Box>
      );
    }

    // Show final result
    const createdCount = importResult ? (importResult.imported_count - (importResult.updated_count || 0)) : 0;
    const updatedCount = importResult?.updated_count || 0;

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        {importResult && (
          <>
            {importResult.status === 'failed' ? (
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            ) : (
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            )}
            <Typography variant="h5" gutterBottom>
              {importResult.status === 'failed' ? t('import_failed') : t('Import complete')}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              {createdCount > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${createdCount} ${t('created')}`}
                  color="success"
                />
              )}
              {updatedCount > 0 && (
                <Chip
                  icon={<EditIcon />}
                  label={`${updatedCount} ${t('updated')}`}
                  color="info"
                />
              )}
              {importResult.skipped_count > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${importResult.skipped_count} ${t('skipped')}`}
                  color="warning"
                />
              )}
              {importResult.error_count > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${importResult.error_count} ${t('errors')}`}
                  color="error"
                />
              )}
            </Box>

            {/* Show details for skipped/error items */}
            {(importResult.skipped_count > 0 || importResult.error_count > 0) && importResult.details && (
              <Box sx={{ mt: 3, textAlign: 'left' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Details')}:
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                  {importResult.details
                    .filter(d => d.status !== 'created' && d.status !== 'updated' && d.status !== 'imported')
                    .map((detail, index) => (
                      <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('Row')} {detail.row}: {detail.message}
                      </Typography>
                    ))
                  }
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderUploadStep();
      case 1: return renderMappingStep();
      case 2: return renderPreviewStep();
      case 3: return renderResultStep();
      default: return null;
    }
  };

  // Prevent closing while import is in progress
  const handleClose = () => {
    if (isImportInProgress) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={currentStep === 3 && !isImportInProgress ? handleClose : undefined}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {t('Import data')}
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={isImportInProgress}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Tabs for entity type selection */}
        {currentStep === 0 && (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                icon={<PersonIcon />}
                iconPosition="start"
                label={t('Customers')}
              />
              <Tab
                icon={<InventoryIcon />}
                iconPosition="start"
                label={t('Parts')}
              />
              <Tab
                icon={<BuildIcon />}
                iconPosition="start"
                label={t('Jobs')}
              />
            </Tabs>

            {/* Info box explaining the import process */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {activeTab === 0
                  ? t('csv_import_customers_info')
                  : activeTab === 1
                    ? t('csv_import_parts_info')
                    : t('csv_import_jobs_info')
                }
              </Typography>
            </Alert>
          </>
        )}

        {/* Stepper */}
        {currentStep > 0 && (
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {STEPS.map((step, index) => (
              <Step key={step}>
                <StepLabel>{getStepLabel(index)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderStepContent()
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {currentStep > 0 && currentStep < 3 && (
          <Button onClick={handleBack} disabled={loading}>
            {t('Back')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {currentStep < 3 && (
          <Button onClick={handleClose} disabled={loading || isImportInProgress}>
            {t('Cancel')}
          </Button>
        )}
        {currentStep === 1 && (
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={loading || !canProceed()}
          >
            {t('Preview')}
          </Button>
        )}
        {currentStep === 2 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={loading || !canProceed()}
          >
            {t('Import')} ({previewSummary?.total || ((previewSummary?.will_import || 0) + (previewSummary?.will_update || 0))})
          </Button>
        )}
        {currentStep === 3 && !isImportInProgress && (
          <Button variant="contained" onClick={handleClose}>
            {t('Close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportDialog;
